import { useEffect, useRef, useState } from 'react';
import type { BoardSummary, Item } from '@scribbleplot/shared';
import { api } from './api';

const cache = new Map<string, Item[]>();
const pending = new Map<string, Promise<Item[]>>();
const queue: (() => void)[] = [];
let active = 0;
function pump() {
  while (active < 4 && queue.length) {
    active++;
    queue.shift()!();
  }
}
function load(board: BoardSummary, userId: string): Promise<Item[]> {
  const key = `${userId}:${board.id}:${board.updatedAt}`;
  if (cache.has(key)) return Promise.resolve(cache.get(key)!);
  if (pending.has(key)) return pending.get(key)!;
  const promise = new Promise<Item[]>((resolve, reject) => {
    queue.push(() => {
      api
        .getBoard(board.id)
        .then(({ board: detail }) => {
          const items = detail.doc.items.slice(0, 120);
          cache.set(key, items);
          if (cache.size > 150) cache.delete(cache.keys().next().value!);
          resolve(items);
        }, reject)
        .finally(() => {
          active--;
          pending.delete(key);
          pump();
        });
    });
  });
  pending.set(key, promise);
  pump();
  return promise;
}
export function useBoardPreview(board: BoardSummary, userId: string) {
  const ref = useRef<HTMLDivElement>(null);
  const [items, setItems] = useState<Item[] | undefined>();
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setItems(undefined);
    setFailed(false);
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        observer.disconnect();
        void load(board, userId)
          .then((value) => {
            if (!cancelled) setItems(value);
          })
          .catch(() => {
            if (!cancelled) setFailed(true);
          });
      },
      { rootMargin: '240px' },
    );
    if (ref.current) observer.observe(ref.current);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [board.id, board.updatedAt, userId]);
  return { ref, items, failed };
}
